import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    CssBaseline,
    Snackbar,
    TextField,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    AtSign,
    BadgeCheck,
    Building2,
    Calendar,
    CheckCircle2,
    ClipboardCheck,
    Clock,
    FileText,
    Globe,
    MapPin,
    MessageCircle,
    Package,
    Phone,
    SearchX,
    Send,
    ShoppingBag,
    Unlink,
    User,
    WifiOff,
    Wallet,
    Wrench,
} from 'lucide-react';
import { httpClient } from './common/httpclient';
import { standaloneFieldSx } from './common/fieldStyles';
import { formatPhone } from './common/formatters';
import { buildAppTheme } from './theme/theme';
import ToastAlert from './component/alert/ToastAlert';
import {
    BrandBar,
    Card,
    CardHead,
    ContactButton,
    FeedbackView,
    Hero,
    InfoLine,
    KeyValue,
    LoadingView,
    MQ_860,
    PoweredBy,
    PortalMain,
    SectionLabel,
    StickyBar,
    SummaryRow,
    TotalBox,
    formatCurrency,
    normalizeStatus,
    parseDateTime,
    parseMoney,
    toInternationalPhone,
} from './component/publicPortal/PublicPortalKit';

/**
 * Portal público do orçamento (`/orcamento/:id?t=<token>`) — o cliente vê o
 * orçamento, aprova (a API gera a ordem de serviço) e envia dúvidas ao lojista.
 *
 * Visual idêntico ao de `CustomerOrderDisplayScreen.js` (tela pública da OS):
 * tema próprio via `prefers-color-scheme`, sem sidebar do ERP, e as peças
 * visuais vêm de `component/publicPortal/PublicPortalKit.js`. Dados e ações
 * vêm de `fv-api/servicequote/public_portal.go`:
 *  - GET  /orcamento/{id}?t=
 *  - POST /orcamento/{id}/aprovar?t=
 *  - POST /orcamento/{id}/duvida?t=  { message }
 */

const MAX_MESSAGE = 1000;
const FOOT_TEXT = 'Acompanhamento de orçamento · FlowVendas';
const STEP_LABELS = ['Elaboração', 'Aguardando aprovação', 'Aprovado'];

const WAITING_META = {
    key: 'aguardando', label: 'Aguardando aprovação', step: 1, Icon: Clock,
    title: 'Orçamento aguardando sua aprovação',
    msg: 'Confira os itens e valores abaixo. Se estiver tudo certo, aprove; se tiver dúvida, fale com a empresa.',
};
const APPROVED_META = {
    key: 'aprovado', label: 'Aprovado', step: 2, Icon: BadgeCheck,
    title: 'Orçamento aprovado',
    msg: 'Obrigado! A empresa já foi avisada e dará andamento ao serviço.',
};

// Chave = status normalizado (sem acento, minúsculo). `approvable` libera o botão.
const STATUS_META = {
    novo: {
        key: 'novo', label: 'Em elaboração', step: 0, Icon: Clock, approvable: true,
        title: 'Orçamento em elaboração',
        msg: 'A empresa ainda está montando este orçamento. Volte a este link a qualquer momento para conferir.',
    },
    'enviado para cliente': { ...WAITING_META, approvable: true },
    'aguardando aprovacao': { ...WAITING_META, approvable: true },
    aprovado: APPROVED_META,
    'ordem gerada': { ...APPROVED_META, label: 'Aprovado · OS gerada' },
};

function getStatusMeta(status) {
    const known = STATUS_META[normalizeStatus(status)];
    if (known) return known;
    return {
        key: 'desconhecido', label: status || '—', step: -2, Icon: Clock,
        title: 'Acompanhe o seu orçamento',
        msg: 'Este orçamento não está disponível para aprovação no momento. Fale com a empresa se tiver dúvidas.',
    };
}

const FEEDBACK = {
    invalid: {
        tone: 'warning', Icon: Unlink, title: 'Este link não é válido',
        text: 'O link pode estar incompleto, ter sido alterado ou ter expirado.',
        help: [
            <>Confira se copiou o link inteiro, até o final (incluindo o código depois de <b>?t=</b>).</>,
            'Abra o link direto da mensagem que você recebeu.',
            'Se continuar com problema, peça um novo link à empresa.',
        ],
    },
    notfound: {
        tone: 'neutral', Icon: SearchX, title: 'Orçamento não encontrado',
        text: 'Não localizamos nenhum orçamento com esse número. O número pode estar incorreto ou o orçamento pode ter sido removido.',
        help: [
            'Confira o link na mensagem que você recebeu.',
            'Entre em contato com a empresa que enviou o link.',
        ],
    },
    error: {
        tone: 'danger', Icon: WifiOff, title: 'Não foi possível carregar',
        text: 'Houve um problema de conexão ou de nosso lado. Seu orçamento não foi alterado.',
        help: null, retry: true,
    },
};

// ---------------------------------------------------------------------------
// Tela (raiz): tema próprio do cliente final
// ---------------------------------------------------------------------------

export default function CustomerQuoteDisplayScreen() {
    const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
    const publicTheme = useMemo(() => buildAppTheme(prefersDark ? 'dark' : 'light'), [prefersDark]);

    return (
        <ThemeProvider theme={publicTheme}>
            <CssBaseline />
            <QuoteScreen />
        </ThemeProvider>
    );
}

function QuoteScreen() {
    const theme = useTheme();
    const { id } = useParams();
    const [queryParams] = useSearchParams();
    const token = queryParams.get('t') || '';

    // loading | ok | invalid | notfound | error
    const [view, setView] = useState('loading');
    const [quote, setQuote] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [approving, setApproving] = useState(false);
    const [question, setQuestion] = useState('');
    const [sending, setSending] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });
    const url = (path = '') => `/orcamento/${id}${path}?t=${encodeURIComponent(token)}`;

    const fetchQuote = useCallback(async ({ silent = false } = {}) => {
        try {
            if (silent) setRefreshing(true);
            const response = await httpClient.get(url());
            if (response.status === 200 && response.data?.items) {
                setQuote(response.data);
                setUpdatedAt(new Date());
                setView('ok');
                if (silent) showToast('Orçamento atualizado');
                return;
            }
            // Refresh manual que falhou: mantém o que já está na tela.
            if (silent) {
                showToast('Não foi possível atualizar agora. Tente novamente.', 'error');
                return;
            }
            if (response.status === 400) setView('invalid');
            else if (response.status === 404) setView('notfound');
            else setView('error');
        } catch (error) {
            console.error('Erro ao carregar orçamento:', error);
            if (silent) showToast('Não foi possível atualizar agora. Tente novamente.', 'error');
            else setView('error');
        } finally {
            if (silent) setRefreshing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, token]);

    useEffect(() => {
        if (id) fetchQuote();
    }, [id, fetchQuote]);

    // Alinha o fundo do <body> (overscroll do celular) com o tema público.
    useEffect(() => {
        const previous = document.body.style.backgroundColor;
        document.body.style.backgroundColor = theme.palette.background.default;
        return () => { document.body.style.backgroundColor = previous; };
    }, [theme.palette.background.default]);

    const handleApprove = async () => {
        if (approving) return;
        setApproving(true);
        try {
            const response = await httpClient.post(url('/aprovar'), {});
            if (response.status === 200) {
                showToast('Orçamento aprovado! A empresa já foi avisada.');
                await fetchQuote();
            } else {
                showToast(typeof response.data === 'string' && response.data.trim() ? response.data : 'Não foi possível aprovar agora. Tente novamente.', 'error');
            }
        } catch (error) {
            console.error('Erro ao aprovar orçamento:', error);
            showToast('Não foi possível aprovar agora. Tente novamente.', 'error');
        } finally {
            setApproving(false);
        }
    };

    const handleSendQuestion = async () => {
        const message = question.trim();
        if (!message || sending) return;
        setSending(true);
        try {
            const response = await httpClient.post(url('/duvida'), { message });
            if (response.status === 201) {
                setQuestion('');
                showToast('Dúvida enviada! A empresa entrará em contato.');
                await fetchQuote();
            } else {
                showToast(typeof response.data === 'string' && response.data.trim() ? response.data : 'Não foi possível enviar agora. Tente novamente.', 'error');
            }
        } catch (error) {
            console.error('Erro ao enviar dúvida:', error);
            showToast('Não foi possível enviar agora. Tente novamente.', 'error');
        } finally {
            setSending(false);
        }
    };

    let content;
    if (view === 'loading') {
        content = <LoadingView text="Carregando seu orçamento…" />;
    } else if (view !== 'ok' || !quote) {
        const kind = view === 'ok' ? 'notfound' : view;
        content = (
            <FeedbackView
                config={FEEDBACK[kind] || FEEDBACK.error}
                footText={FOOT_TEXT}
                onRetry={() => { setView('loading'); fetchQuote(); }}
            />
        );
    } else {
        content = (
            <QuoteView
                quote={quote}
                updatedAt={updatedAt}
                refreshing={refreshing}
                onRefresh={() => fetchQuote({ silent: true })}
                approving={approving}
                onApprove={handleApprove}
                question={question}
                setQuestion={setQuestion}
                sending={sending}
                onSendQuestion={handleSendQuestion}
            />
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', display: 'flex', flexDirection: 'column',
                '@media print': { '& .no-print': { display: 'none !important' } },
            }}
        >
            {content}
            <Snackbar
                open={toast.open}
                autoHideDuration={2800}
                onClose={(event, reason) => {
                    if (reason === 'clickaway') return;
                    setToast((prev) => ({ ...prev, open: false }));
                }}
                className="no-print"
                sx={{ bottom: { xs: 88, md: 24 } }}
            >
                <ToastAlert severity={toast.severity}>{toast.message}</ToastAlert>
            </Snackbar>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Estado "ok"
// ---------------------------------------------------------------------------

function QuoteView({ quote, updatedAt, refreshing, onRefresh, approving, onApprove, question, setQuestion, sending, onSendQuestion }) {
    const meta = getStatusMeta(quote.status);
    const canApprove = !!meta.approvable;
    const isApproved = meta.key === 'aprovado';
    const values = quote.values || {};
    const items = quote.items || [];
    const services = items.filter((item) => item.type === 'service');
    const products = items.filter((item) => item.type !== 'service');
    const itemCount = items.length;
    const showProductDiscount = parseMoney(values.total_product_item_discount) > 0;

    const data = parseDateTime(quote.quote_date);
    const hasVehicle = quote.vehicle_name && quote.vehicle_name !== '-';
    const hasPlate = quote.vehicle_plate && quote.vehicle_plate !== '-';

    const cityState = [quote.enterprise_city, quote.enterprise_state].filter(Boolean).join('/');
    const addressText = [quote.enterprise_address, cityState].filter(Boolean).join(' - ');
    const waHref = quote.enterprise_phone
        ? `https://wa.me/${toInternationalPhone(quote.enterprise_phone)}?text=${encodeURIComponent(`Olá! Tenho uma dúvida sobre o orçamento nº ${quote.number}.`)}`
        : '';
    const telHref = quote.enterprise_phone ? `tel:+${toInternationalPhone(quote.enterprise_phone)}` : '';

    const tiles = [
        {
            icon: <Calendar size={12} />,
            label: 'Data',
            content: data
                ? <><b>{data.dow}, {data.date}</b>{data.time && <small>às {data.time}</small>}</>
                : <b>{quote.quote_date || '—'}</b>,
        },
        {
            icon: <Wallet size={12} />,
            label: 'Valor total',
            content: <><b>{formatCurrency(values.amount)}</b><small>{itemCount} {itemCount === 1 ? 'item' : 'itens'}</small></>,
        },
    ];

    const approveButton = (large) => (
        <Button
            fullWidth
            variant="contained"
            startIcon={approving ? <CircularProgress size={18} color="inherit" /> : <CheckCircle2 size={large ? 17 : 18} />}
            disabled={approving || itemCount === 0}
            onClick={onApprove}
            sx={{ flex: 1, whiteSpace: 'nowrap', fontSize: 13, fontWeight: 700, borderRadius: '11px', p: large ? '12px 14px' : '12px 16px' }}
        >
            Aprovar orçamento
        </Button>
    );

    return (
        <>
            <BrandBar name={quote.enterprise_name || 'Orçamento'} logo={quote.enterprise_logo} phone={quote.enterprise_phone} />

            <PortalMain>
                <Hero
                    eyebrow="Orçamento"
                    number={quote.number}
                    meta={meta}
                    tiles={tiles}
                    stepLabels={STEP_LABELS}
                    stepDone={isApproved}
                    footLeft={updatedAt ? `Atualizado às ${updatedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}
                    onRefresh={onRefresh}
                    refreshing={refreshing}
                    refreshLabel="Atualizar orçamento"
                />

                <Box
                    sx={{
                        display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: '14px',
                        [MQ_860]: { gridTemplateColumns: 'minmax(0,1fr) 340px', gap: '18px', alignItems: 'start' },
                    }}
                >
                    {/* Mobile: as colunas "somem" (display: contents) e cada card se ordena por `order`. */}
                    <Box sx={{ display: 'contents', [MQ_860]: { display: 'flex', flexDirection: 'column', gap: '18px' } }}>
                        <Card sx={{ order: 1 }}>
                            <CardHead icon={<User size={16} />} title="Dados do orçamento" />
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                                <KeyValue label="Cliente" value={quote.customer_name || 'Não informado'} full />
                                {hasVehicle && (
                                    <KeyValue label="Veículo" value={quote.vehicle_name} full={!hasPlate} />
                                )}
                                {hasPlate && <KeyValue label="Placa" value={quote.vehicle_plate} />}
                            </Box>
                        </Card>

                        {quote.defect_reason && (
                            <Card sx={{ order: 2 }}>
                                <CardHead icon={<ClipboardCheck size={16} />} title="Defeito relatado" />
                                <Typography sx={{ m: 0, fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {quote.defect_reason}
                                </Typography>
                            </Card>
                        )}
                        {quote.observations && (
                            <Card sx={{ order: 2 }}>
                                <CardHead icon={<FileText size={16} />} title="Observações" />
                                <Typography sx={{ m: 0, fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {quote.observations}
                                </Typography>
                            </Card>
                        )}

                        <Card sx={{ order: 3 }}>
                            <CardHead
                                icon={<ShoppingBag size={16} />}
                                tone="purple"
                                title="Itens do orçamento"
                                count={`${itemCount} ${itemCount === 1 ? 'item' : 'itens'}`}
                            />
                            {services.length > 0 && (
                                <>
                                    <SectionLabel first>Serviços</SectionLabel>
                                    {services.map((item, index) => <ItemRow key={`s-${item.name}-${index}`} item={item} isService />)}
                                </>
                            )}
                            {products.length > 0 && (
                                <>
                                    <SectionLabel first={services.length === 0}>Produtos e peças</SectionLabel>
                                    {products.map((item, index) => <ItemRow key={`p-${item.name}-${index}`} item={item} />)}
                                </>
                            )}
                            {itemCount === 0 && (
                                <Box sx={{ textAlign: 'center', color: 'text.disabled', fontSize: 12.5, py: '26px', px: '10px' }}>
                                    Este orçamento ainda não possui itens.
                                </Box>
                            )}
                        </Card>

                        <Card sx={{ order: 5 }}>
                            <CardHead icon={<MessageCircle size={16} />} title="Tirar dúvidas" />
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Sua dúvida"
                                placeholder="Escreva aqui sua pergunta sobre o orçamento"
                                sx={standaloneFieldSx}
                                value={question}
                                onChange={(e) => setQuestion(e.target.value.slice(0, MAX_MESSAGE))}
                                disabled={sending}
                                helperText={`${question.length}/${MAX_MESSAGE}`}
                            />
                            <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', mt: '14px' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
                                    disabled={sending || !question.trim()}
                                    onClick={onSendQuestion}
                                    sx={{ fontSize: 13, fontWeight: 700, borderRadius: '11px', p: '11px 16px' }}
                                >
                                    Enviar dúvida
                                </Button>
                            </Box>

                            {quote.messages && quote.messages.length > 0 && (
                                <>
                                    <SectionLabel>Suas dúvidas enviadas</SectionLabel>
                                    {quote.messages.map((m) => (
                                        <Box
                                            key={m.id}
                                            sx={(t) => ({
                                                bgcolor: 'background.subtle', border: `1px solid ${t.palette.divider}`,
                                                borderRadius: '11px', p: '10px 12px', mb: '8px',
                                            })}
                                        >
                                            <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.disabled', mb: '3px' }}>
                                                {new Date(m.created_at).toLocaleString('pt-BR')}
                                            </Typography>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.message}</Typography>
                                        </Box>
                                    ))}
                                </>
                            )}
                        </Card>
                    </Box>

                    <Box sx={{ display: 'contents', [MQ_860]: { display: 'flex', flexDirection: 'column', gap: '18px', position: 'sticky', top: 16 } }}>
                        <Card sx={{ order: 4 }}>
                            <CardHead icon={<FileText size={16} />} title="Resumo de valores" />
                            <SummaryRow label="Serviços" value={formatCurrency(values.amount_services)} />
                            <SummaryRow label="Produtos e peças" value={formatCurrency(values.amount_products)} />
                            {showProductDiscount && (
                                <SummaryRow discount label="Desconto em produtos" value={`−${formatCurrency(values.total_product_item_discount)}`} />
                            )}
                            <TotalBox value={formatCurrency(values.amount)} />

                            {canApprove ? (
                                <Box className="no-print" sx={{ display: 'none', mt: '14px', [MQ_860]: { display: 'flex' } }}>
                                    {approveButton(false)}
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex', alignItems: 'flex-start', gap: '10px', mt: '14px', p: '12px 14px', borderRadius: '12px',
                                        bgcolor: isApproved ? 'success.bg' : 'background.subtle',
                                        color: isApproved ? 'success.main' : 'text.secondary',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexShrink: 0, mt: '1px' }}><FileText size={16} /></Box>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45, color: 'inherit' }}>
                                        {isApproved
                                            ? 'Este orçamento foi aprovado. A empresa dará andamento ao serviço.'
                                            : 'Este orçamento não está disponível para aprovação.'}
                                    </Typography>
                                </Box>
                            )}
                        </Card>

                        <Card sx={{ order: 7 }}>
                            <CardHead icon={<Building2 size={16} />} title="Fale com a empresa" />
                            {(waHref || telHref) && (
                                <Box sx={{ display: 'none', gap: '8px', mb: '14px', [MQ_860]: { display: 'flex' } }}>
                                    {waHref && <ContactButton kind="whatsapp" href={waHref} />}
                                    {telHref && <ContactButton kind="phone" href={telHref} />}
                                </Box>
                            )}
                            <InfoLine icon={<MapPin size={15} />}>{addressText || 'Endereço não informado'}</InfoLine>
                            {quote.enterprise_phone && (
                                <InfoLine icon={<Phone size={15} />}>{formatPhone(quote.enterprise_phone)}</InfoLine>
                            )}
                            {quote.enterprise_facebook && <InfoLine icon={<Globe size={15} />}>{quote.enterprise_facebook}</InfoLine>}
                            {quote.enterprise_instagram && <InfoLine icon={<AtSign size={15} />}>{quote.enterprise_instagram}</InfoLine>}
                        </Card>
                    </Box>
                </Box>
            </PortalMain>

            <PoweredBy text={FOOT_TEXT} />

            {(canApprove || waHref) && (
                <StickyBar>
                    {canApprove && approveButton(true)}
                    {waHref && !canApprove && <ContactButton kind="whatsapp" href={waHref} large />}
                    {waHref && !canApprove && telHref && <ContactButton kind="phone" href={telHref} large />}
                </StickyBar>
            )}
        </>
    );
}

/** Linha de item (serviço/produto) — mesma anatomia do `ItemRow` da OS. */
function ItemRow({ item, isService }) {
    const discount = parseMoney(item.discount_amount) > 0 ? item.discount_amount : null;

    return (
        <Box sx={(t) => ({ border: `1px solid ${t.palette.divider}`, borderRadius: '12px', mb: '8px', overflow: 'hidden', bgcolor: 'background.paper' })}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '12px' }}>
                <Box
                    sx={{
                        width: 38, height: 38, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: isService ? 'secondary.bg' : 'info.bg', color: isService ? 'secondary.main' : 'primary.main',
                    }}
                >
                    {isService ? <Wrench size={17} /> : <Package size={17} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word', [MQ_860]: { fontSize: 14 } }}>
                        {item.name}
                    </Typography>
                    <Box sx={{ fontSize: 11.5, color: 'text.disabled', mt: '2px' }}>
                        <span>{item.quantity} × {formatCurrency(item.amount_unit)}</span>
                    </Box>
                    {discount && (
                        <Typography sx={{ fontSize: 11, color: 'error.main', mt: '2px', fontWeight: 600 }}>
                            Desconto no item: −{formatCurrency(discount)}
                        </Typography>
                    )}
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: 13.5, flexShrink: 0, textAlign: 'right' }}>
                    {formatCurrency(item.amount_total)}
                </Typography>
            </Box>
        </Box>
    );
}
